import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Subscribe to a CSS media query. SSR-safe and tolerant of jsdom (which lacks
 * `addEventListener` on the legacy MediaQueryList in some versions).
 */
export function useMediaQuery(query: string): boolean {
  const getMatch = useCallback(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  }, [query]);

  const [matches, setMatches] = useState(getMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = (): void => setMatches(mql.matches);
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, [query]);

  return matches;
}

/** True when the viewport is at icon-rail width or narrower (≤1024px). */
export function useIsNarrow(): boolean {
  return useMediaQuery('(max-width: 1024px)');
}

/** True when the user has requested reduced motion. */
export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

/** Run `handler` when a click lands outside any of the referenced elements. */
export function useOnClickOutside(
  refs: React.RefObject<HTMLElement | null>[],
  handler: () => void,
  enabled = true,
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;
    const onPointer = (event: MouseEvent): void => {
      const target = event.target as Node;
      const inside = refs.some((ref) => ref.current?.contains(target));
      if (!inside) handlerRef.current();
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [refs, enabled]);
}

/** Call `handler` when the Escape key is pressed while `enabled`. */
export function useEscapeKey(handler: () => void, enabled = true): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') handlerRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled]);
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]';

/**
 * O que o Tab alcança dentro de `container`. `tabIndex < 0` fica de fora mesmo
 * quando o seletor casa (um botão com `tabindex="-1"` continua sendo `button`):
 * as opções da paleta de comandos são assim, e contá-las como "último da fila"
 * deixaria o Tab escapar do painel.
 */
function tabbables(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.tabIndex >= 0,
  );
}

/**
 * Trap focus inside `container` while `active`, and return it on cleanup to the
 * element that held it before. Used by Dialog.
 *
 * O contêiner é o ELEMENTO, não uma ref: os overlays montam o painel dentro de um
 * Portal, que só o põe no DOM no commit seguinte ao da abertura. Com uma ref, o
 * efeito rodava com ela vazia e nunca mais rodava — a trava não engatava. Com o
 * elemento como dependência (um `ref={setPanel}` de estado), ela engata quando o
 * painel existe.
 */
export function useFocusTrap(container: HTMLElement | null, active: boolean): void {
  useEffect(() => {
    if (!active || !container) return;

    // Um campo com autoFocus pode ter recebido o foco ao montar: ele fica com o
    // foco, e quem estava antes já não é conhecível — não há o que devolver.
    const focusedInside = container.contains(document.activeElement);
    const previouslyFocused = focusedInside ? null : (document.activeElement as HTMLElement | null);
    if (!focusedInside) (tabbables(container)[0] ?? container).focus();

    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;
      const focusable = tabbables(container).filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const activeEl = document.activeElement;
      if (event.shiftKey && activeEl === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeEl === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', onKey);
    return () => {
      container.removeEventListener('keydown', onKey);
      previouslyFocused?.focus?.();
    };
  }, [active, container]);
}

/** O contêiner em que o React monta o app (`index.html` / `main.tsx`). */
const APP_ROOT_ID = 'root';

/**
 * Enquanto `active`, torna `inert` o app por trás de um overlay modal: os filhos
 * de `#root` que não contêm `overlay`. `aria-modal` só pede ao leitor de tela que
 * ignore o resto; `inert` tira o resto do Tab, do clique e da árvore acessível de
 * verdade. Os overlays moram num Portal em `body`, fora de `#root` — por isso
 * toasts, tooltips e menus (também em Portais) seguem vivos e anunciados.
 *
 * Só remove o `inert` que ele mesmo pôs: um overlay aberto sobre outro não
 * devolve ao app a interação que o de baixo tirou. Efeito de LAYOUT de propósito:
 * a limpeza roda antes dos efeitos comuns, então o `inert` sai antes de
 * {@link useFocusTrap} devolver o foco — um elemento inerte recusa o foco em
 * silêncio, e o foco cairia no `body`.
 */
export function useInertOutside(active: boolean, overlay: HTMLElement | null): void {
  useLayoutEffect(() => {
    if (!active || !overlay) return;
    const root = document.getElementById(APP_ROOT_ID);
    if (!root) return;
    const blocked = Array.from(root.children).filter(
      (el) => !el.contains(overlay) && !el.hasAttribute('inert'),
    );
    blocked.forEach((el) => el.setAttribute('inert', ''));
    return () => blocked.forEach((el) => el.removeAttribute('inert'));
  }, [active, overlay]);
}

/** Lock `document.body` scroll while `locked` (overlay open). */
export function useBodyScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [locked]);
}

/** Debounce a value by `delayMs`. Useful for search-as-you-type inputs. */
export function useDebouncedValue<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}
